import { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import MainLayout from '../components/layout/MainLayout'
import ProjectAgents from '../components/projects/ProjectAgents'
import ProjectCreator from '../components/projects/ProjectCreator'
import ProjectDocuments from '../components/projects/ProjectDocuments'
import ProjectDonors from '../components/projects/ProjectDonors'
import ProjectFindings from '../components/projects/ProjectFindings'
import ProjectHero from '../components/projects/ProjectHero'
import ProjectMembers from '../components/projects/ProjectMembers'
import ProjectMessages from '../components/projects/ProjectMessages'
import ProjectOverview from '../components/projects/ProjectOverview'
import ProjectQuestions from '../components/projects/ProjectQuestions'
import ProjectSummary from '../components/projects/ProjectSummary'
import ProjectTabs from '../components/projects/ProjectTabs'
import ProjectTasks from '../components/projects/ProjectTasks'
import ProjectTimeline from '../components/projects/ProjectTimeline'
import ResearchActiveAgents from '../components/projects/ResearchActiveAgents'
import ResearchBudgetTracker from '../components/projects/ResearchBudgetTracker'
import ErrorMessage from '../components/shared/ErrorMessage'
import LoadingSpinner from '../components/shared/LoadingSpinner'
import { useAuth } from '../contexts/AuthContext'
import './ProjectDetail.css'

import { API_URL } from '../config/api'
interface ProjectData {
  id: number
  title: string
  description: string
  problem_statement: string
  methodology: string
  expected_outcomes: string
  project_type: 'mission' | 'research'
  status: 'planning' | 'active' | 'paused' | 'completed'
  visibility: 'public' | 'restricted' | 'private'
  category: string
  budget_flz: number
  spent_flz: number
  progress: number
  owner_id: number
  owner_name: string
  members_count: number
  agents_count: number
  agent_count: number
  tasks_count: number
  findings_count: number
  questions_count: number
  messages_count: number
  donors_count: number
  followers_count: number
  cycles_count: number
  likes_count: number
  saves_count: number
  views_count: number
  created_at: string
  updated_at: string
}

type TabType = 'overview' | 'summary' | 'tasks' | 'findings' | 'questions' | 'members' | 'agents' | 'messages' | 'timeline' | 'documents' | 'active-agents' | 'budget' | 'creator' | 'donors'

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const { user: currentUser } = useAuth()
  const projectId = parseInt(id || '0', 10)
  const isResearchProject = location.pathname.startsWith('/research/')

  const [project, setProject] = useState<ProjectData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<TabType>('overview')

  const loadProject = async () => {
    try {
      setLoading(true)
      setError(null)
      const token = localStorage.getItem('token')
      const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}
      const endpoint = isResearchProject
        ? `${API_URL}/api/research/projects/${projectId}`
        : `${API_URL}/api/projects/${projectId}`
      const response = await fetch(endpoint, { headers })
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Project not found')
        }
        throw new Error('Failed to load project')
      }
      const data = await response.json() as { project?: ProjectData } & ProjectData
      setProject(data.project ?? data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load project')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadProject()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, isResearchProject])

  const isOwner = Boolean(currentUser && project && String(currentUser.id) === String(project.owner_id))

  if (loading) {
    return (
      <MainLayout>
        <div className="project-detail-page">
          <LoadingSpinner />
        </div>
      </MainLayout>
    )
  }

  if (error || !project) {
    return (
      <MainLayout>
        <div className="project-detail-page">
          <ErrorMessage message={error || 'Project not found'} onRetry={() => { void loadProject() }} />
        </div>
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      <div className="project-detail-page">
        <div className="project-detail-container">
          {/* Hero Section */}
          <ProjectHero project={project} isOwner={isOwner} />

          {/* Tab Menu */}
          <ProjectTabs
            activeTab={activeTab}
            onTabChange={setActiveTab}
            projectType={project.project_type}
            tasksCount={project.tasks_count || 0}
            findingsCount={project.findings_count || 0}
            membersCount={project.members_count || 0}
            agentsCount={project.agents_count || 0}
            donorsCount={project.donors_count || 0}
            questionsCount={project.questions_count || 0}
          />

          {/* Tab Content */}
          <div className="project-content">
            {activeTab === 'overview' && <ProjectOverview project={project} />}
            {activeTab === 'summary' && <ProjectSummary projectId={projectId} isResearch={isResearchProject} />}
            {activeTab === 'tasks' && <ProjectTasks projectId={projectId} isResearch={isResearchProject} />}
            {activeTab === 'findings' && <ProjectFindings projectId={projectId} isResearch={isResearchProject} />}
            {activeTab === 'questions' && <ProjectQuestions projectId={projectId} isResearch={isResearchProject} />}
            {activeTab === 'active-agents' && <ResearchActiveAgents researchId={projectId} />}
            {activeTab === 'budget' && <ResearchBudgetTracker researchId={projectId} />}
            {activeTab === 'documents' && <ProjectDocuments projectId={projectId} isResearch={isResearchProject} />}
            {activeTab === 'members' && <ProjectMembers projectId={projectId} isResearch={isResearchProject} />}
            {activeTab === 'agents' && <ProjectAgents projectId={projectId} isResearch={isResearchProject} />}
            {activeTab === 'messages' && <ProjectMessages projectId={projectId} isResearch={isResearchProject} />}
            {activeTab === 'timeline' && <ProjectTimeline projectId={projectId} isResearch={isResearchProject} />}
            {activeTab === 'creator' && <ProjectCreator projectId={projectId} isResearch={isResearchProject} />}
            {activeTab === 'donors' && <ProjectDonors projectId={projectId} isResearch={isResearchProject} />}
          </div>
        </div>
      </div>
    </MainLayout>
  )
}
